import React, { useEffect, useMemo, useState } from 'react';
import CustomButton from '../../shared/custom-button/custom-button';
import './policy-action.scss';
import { Checkbox, Modal } from 'antd';
import FormField from '../../shared/edit-row-modal/form-field/form-field';
import { ILLEGAL_CHARACTERS_MESSAGE, validateString } from '../../../utils/validationUtils';
import { CustomInput } from 'src/components/shared/custom-input/custom-input';
import { MultiSelectConnections } from '../../shared/multi-select-connections/multi-select-connections';
import { useStores } from '../../store';
import { DateTimePicker } from './date-time-picker/date-time-picker';
import { getInitializedDateTimeForPolicy } from '../../../utils/datesUtils';
import GenericModal from '../../shared/generic-modal/generic-modal';

export interface PolicyActionDto {
    policyName?: string;
    policyComments?: string;
    externalId?: string;
    selectedConnections?: number[];
    scheduleDate?: Date;
}

interface PolicyActionModalProps {
    visible: boolean;
    close: () => void;
    ok: (actionData: PolicyActionDto) => void;
    title?: string
    okBtnLabel?: string
    initialData?: PolicyActionDto;
    className?: string;
    children?: JSX.Element;
    displayExtraOptions?: boolean;
}

export const PolicyActionModal: React.FC<PolicyActionModalProps> = ({ visible,
                                                                        close,
                                                                        ok,
                                                                        title = 'Apply Policy',
                                                                        okBtnLabel,
                                                                        initialData,
                                                                        className,
                                                                        children,
                                                                        displayExtraOptions = false }) => {
    const { customerStore, fwVersionStore } = useStores();

    const [policyName, setPolicyName] = useState<any>('');
    const [policyComments, setPolicyComments] = useState<any>('');
    const [applyLater, setApplyLater] = useState(false);
    const [applyToOtherConnections, setApplyToOtherConnections] = useState(false);
    const [selectedConnections, setSelectedConnections] = useState<number[]>([]);
    const [selectedDateTime, setSelectedDateTime] = useState<Date>(new Date());
    const [showConfirmUncheckModal, setShowConfirmUncheckModal] = useState(false);
    const [isDiscarding, setIsDiscarding] = useState(false);


    const onClick = () => {
        const data: PolicyActionDto = {
            policyName,
            policyComments,
            externalId: initialData?.externalId
        };
        if (applyToOtherConnections) {
            data.selectedConnections = selectedConnections;
        }
        if (applyLater) {
            data.scheduleDate = selectedDateTime;
        }
        ok(data);
        close();
    };

    const onClose = () => {
        close();
    };

    useEffect(() => {
        if (visible) {
            const { policyComments, policyName, selectedConnections, scheduleDate } = initialData ?? {};
            setPolicyName(policyName ?? '');
            setPolicyComments(policyComments ?? '');
            setApplyLater(!!scheduleDate);
            setApplyToOtherConnections(!!selectedConnections?.length);
            setSelectedConnections(selectedConnections?.length ? selectedConnections : []);
            setSelectedDateTime(scheduleDate ?? getInitializedDateTimeForPolicy());
            setShowConfirmUncheckModal(false);
            setIsDiscarding(false);
        }
    }, [visible]);

    const applyBtnLabel = useMemo(() => {
        return applyLater ? 'Schedule Apply' : 'Apply';
    }, [applyLater]);

    const nameError = useMemo(() =>
        !policyName || !validateString(policyName, false, ['@','#','$','%','&','*','+','/']) ? '' : ILLEGAL_CHARACTERS_MESSAGE,
        [policyName]);

    const commentsError = useMemo(() =>
        !policyComments || !validateString(policyComments, true, ['@','#','$','%','&','*','+','/']) ? '' : ILLEGAL_CHARACTERS_MESSAGE,
        [policyComments]);

    const toggleApplyToOthers = () => {
        setApplyToOtherConnections(!applyToOtherConnections);
        setSelectedConnections([]);
    };

    const handleApplyLaterChange = (value: boolean) => {
        if (!initialData?.scheduleDate) {
            setApplyLater(value);
        } else if (applyLater) {
            setShowConfirmUncheckModal(true);
        } else {
            setApplyLater(true);
        }
    };

    const handleConfirmUncheck = async () => {
        setIsDiscarding(true);
        await fwVersionStore.discardSchedule();
        setIsDiscarding(false);
        setShowConfirmUncheckModal(false);
        setApplyLater(false);
    };

    const handleCancelUncheck = () => {
        setShowConfirmUncheckModal(false);
    };

    const confirmModalButtons = useMemo(() => [
        {
            text: 'Cancel',
            onClick: handleCancelUncheck,
            className: 'secondary-btn',
            disabled: isDiscarding
        },{
            text: 'Continue',
            onClick: () => handleConfirmUncheck(),
            className: `primary-btn ${isDiscarding?'loading':''}`,
            disabled: isDiscarding,
        }], [isDiscarding]);

    return <>
        <Modal open={visible} className={`action-policy-modal-wrapper ${className}`} width={600} closable={false}
               footer={null} destroyOnClose centered>
            <div className="action-policy-modal">
                <div className="action-policy-modal-header">
                    <div className="title">{title}</div>
                </div>
                <div className="custom-modal-content">
                    <div className="modal-message-container">
                        <div className="modal-commit-message">
                            {children ?? 'You are about to apply a new security policy.\n' +
                                'It will take a few minutes for the new policy to be active.'}
                        </div>
                        <FormField label={'Policy Name'} errorMessage={nameError}>
                            <CustomInput inputType="INPUT"
                                         className="policy-name"
                                         hideWarnMessage={!!nameError}
                                         value={policyName}
                                         placeholder={'Add policy name to easily identify the policy (optional)'}
                                         onChange={(e) => setPolicyName(e.target.value)}
                                         maxLength={25}/>
                        </FormField>
                        <FormField label={'Comments'} errorMessage={commentsError}>
                            <CustomInput inputType="TEXTAREA"
                                         className="policy-comment"
                                         hideWarnMessage={!!commentsError}
                                         value={policyComments}
                                         placeholder={'Add a comment to describe the changes (optional)'}
                                         onChange={(e) => setPolicyComments(e.target.value)}
                                         maxLength={250}
                                         rows={3}/>
                        </FormField>
                    </div>
                    {displayExtraOptions && <div className="apply-possibilities">
                        <div className="apply-later-container">
                            <Checkbox checked={applyLater}
                                      disabled={customerStore.isTargetScheduledVersion}
                                      onChange={(e) => handleApplyLaterChange(e.target.checked)}>
                                Apply Later
                            </Checkbox>
                            {applyLater && (
                                <div className="apply-later-section">
                                    <DateTimePicker
                                        value={selectedDateTime}
                                        onChange={setSelectedDateTime}/>
                                </div>
                            )}
                        </div>
                        {!customerStore.isOneConnectionNonBronze && <div className="apply-others-container">
                            <Checkbox
                                checked={applyToOtherConnections}
                                onChange={toggleApplyToOthers}

                            >
                                Apply to other connections
                            </Checkbox>
                            {applyToOtherConnections && (
                                <MultiSelectConnections sourceSid={customerStore.selectedLocation?.serviceId}
                                                        defaultConnection={customerStore.selectedLocation}
                                                        selectedConnections={selectedConnections}
                                                        setSelectedConnections={setSelectedConnections} isScheduled={applyLater} />
                            )}
                        </div>}
                    </div>}
                </div>
                <div className="action-policy-modal-btns custom-modal-footer">
                    <CustomButton className="custom-btn secondary-btn large-btn" label="Cancel" onClick={onClose}/>
                    <CustomButton className="custom-btn primary-btn large-btn" label={okBtnLabel ?? applyBtnLabel}
                                  isDisabled={!!(commentsError || nameError)} onClick={onClick}/>
                </div>
            </div>
        </Modal>
        <GenericModal open={showConfirmUncheckModal}
                      body={<div>Scheduled apply policy will be overridden. Are you sure?</div>}
                      onCancel={close}
                      title={'Cancel Scheduled Apply?'}
                      actionButtons={confirmModalButtons}
                      wrapClassName={'discard-policy-modal'}
                      destroyOnClose={true}
        />
    </>;
};